export const GA_MEASUREMENT_ID = 'G-8DCGJ38NGV';

export const pageview = (url) => {
  if (window.gtag) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: url,
    });
  }
};

// ── Ecommerce ────────────────────────────────────────────────────────────────
//
// GA4 has been installed since long before this file grew a second function,
// and until now it fired page views and nothing else. That means the property
// held no revenue, no product performance and no channel-to-revenue reporting
// — the reports that make GA4 worth having on a storefront were all empty, and
// the questions they answer had to be asked of DynamoDB by hand instead.
//
// These are the four standard GA4 ecommerce events, named exactly as GA4
// expects them. The names are not ours to choose: `view_item_list`,
// `select_item`, `view_item` and `purchase` are what populate the built-in
// Monetisation and Ecommerce reports, and a custom name would land in the
// property as an unremarkable custom event that no report knows about.
//
// Every function is a no-op when gtag is absent — an ad blocker, a consent
// tool, or a test environment — so no caller needs to guard.

const send = (event, params) => {
  try {
    if (window.gtag) window.gtag('event', event, params);
  } catch {
    /* analytics must never break the page it is describing */
  }
};

// GA4 items cap out at 200 per event and charge us nothing for being tidy;
// a marketplace page of 24 is well inside that, but Load More can accumulate.
const MAX_ITEMS = 50;

const toItem = (asset, index) => ({
  item_id: asset.asset_id || asset.slug,
  item_name: asset.title,
  item_category: asset.category || undefined,
  item_brand: asset.creator_name || undefined,
  price: Number(asset.price) || 0,
  currency: 'INR',
  ...(typeof index === 'number' ? { index } : {}),
});

/** A grid of products was shown. `listId` distinguishes the marketplace from a
 *  creator's own page, which is the split the platform could not previously
 *  make anywhere. */
export const viewItemList = (assets, listId, listName) => {
  if (!assets?.length) return;
  send('view_item_list', {
    item_list_id: listId,
    item_list_name: listName,
    items: assets.slice(0, MAX_ITEMS).map(toItem),
  });
};

/** A product card was clicked out of a list. */
export const selectItem = (asset, listId, listName, index) => {
  if (!asset) return;
  send('select_item', {
    item_list_id: listId,
    item_list_name: listName,
    items: [toItem(asset, index)],
  });
};

/** A product page was opened. */
export const viewItem = (asset, src) => {
  if (!asset) return;
  send('view_item', {
    currency: 'INR',
    value: Number(asset.price) || 0,
    // Not a GA4 reserved parameter — registered as a custom dimension it
    // answers "which surface sent this view?" inside GA4 itself, without
    // needing the backend counters.
    mintlink_source: src || 'direct',
    items: [toItem(asset)],
  });
};

/** A purchase completed. `transactionId` must be the real order id: GA4
 *  de-duplicates on it, and a missing one double-counts every refresh of the
 *  success page. */
export const purchase = (asset, transactionId, valueRupees, src) => {
  if (!asset || !transactionId) return;
  send('purchase', {
    transaction_id: transactionId,
    currency: 'INR',
    value: Number(valueRupees) || 0,
    mintlink_source: src || 'direct',
    items: [toItem(asset)],
  });
};

export const search = (term, resultCount) => {
  if (!term) return;
  send('search', { search_term: term, mintlink_result_count: resultCount ?? undefined });
};
